import React, { ReactNode } from "react"

import { Link } from "expo-router"

import { Box, HStack, Heading, Spinner, Text, VStack } from "wealthmode-frontend/gluestack/ui"

export type DetailField = {
  label: string
  value: ReactNode
}

export type DetailLink = {
  href: string
  label: string
}

function FieldRow({ field }: { field: DetailField }) {
  const value = field.value

  return (
    <HStack className="gap-2 items-start">
      <Text className="text-foreground-tertiary w-64 shrink-0">{field.label}</Text>
      {typeof value === "string" || typeof value === "number" ? (
        <Text className="text-foreground-secondary">{value}</Text>
      ) : (
        value
      )}
    </HStack>
  )
}

/**
 * A resource sitting between two list pages: the row the list showed, laid out
 * vertically, plus the links onward to its own subpages. The links are the
 * point - they are what lets you move between an account's subpages without
 * walking back through the account list.
 */
export default function ResourceDetail({
  title,
  subtitle,
  loading,
  fields,
  links,
  children,
}: {
  title: string
  subtitle?: string
  loading: boolean
  fields: DetailField[]
  links: DetailLink[]
  /** Rendered below the links, for whatever the page edits in place. */
  children?: ReactNode
}) {
  return (
    <Box className="flex-1 min-h-0 overflow-y-auto p-4">
      {loading ? (
        <Spinner size={64} />
      ) : (
        <VStack className="gap-8 max-w-3xl shrink-0">
          <Heading size="xl">
            {title} {subtitle ? <Text className="text-sm text-foreground-secondary">{subtitle}</Text> : null}
          </Heading>

          <VStack className="gap-2">
            <Heading size="md">Details</Heading>
            {fields.map((field) => (
              <FieldRow
                key={field.label}
                field={field}
              />
            ))}
          </VStack>

          {links.length === 0 ? null : (
            <VStack className="gap-2">
              <Heading size="md">Links</Heading>
              <VStack className="gap-1 items-start">
                {links.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                  >
                    <Text className="text-markdown-link hover:opacity-80 underline">{link.label}</Text>
                  </Link>
                ))}
              </VStack>
            </VStack>
          )}

          {children}
        </VStack>
      )}
    </Box>
  )
}
